import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import { getSharedChart, ownsShare, stopSharing } from '../../services/chartService';
import { bigThreeLine } from '../../data/astro/bigThree';
import BigThree from './BigThree';
import BirthChartTool from './BirthChartTool';
import { TarotCards } from './ChartTarot';
import { wrapReading } from './ReadingText';
import './BirthChart.css';

const SharedChart = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [owner, setOwner] = useState(false);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return undefined;
    let live = true;
    setOwner(ownsShare(id));
    getSharedChart(id)
      .then((result) => { if (live) setData(result); })
      .catch((err) => {
        if (!live) return;
        setError(err.status === 404 ? 'This chart is no longer shared.' : 'This chart could not be loaded. Try again in a moment.');
      });
    return () => { live = false; };
  }, [id]);

  const stop = async () => {
    try {
      await stopSharing(id);
      setStopped(true);
    } catch (err) {
      setError(err.message || 'Sharing could not be stopped.');
    }
  };

  const chart = data?.chart;
  const line = chart ? bigThreeLine(chart.placements, chart.time_unknown, chart.moon_change) : '';
  const heading = chart?.name ? `${chart.name}'s birth chart` : 'A shared birth chart';
  const shareUrl = `/birth-chart/shared/${id}/`;

  return (
    <div className="birth-page">
      <Helmet>
        <title>{chart ? `${heading}: ${line} | NowTarot` : 'Shared birth chart | NowTarot'}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="birth-wide">
        <div className="birth-tool">
          {stopped ? (
            <header className="birth-intro">
              <p className="birth-eyebrow">Shared birth chart</p>
              <h1>You stopped sharing this chart.</h1>
              <p className="birth-lede">The link no longer works for anyone.</p>
            </header>
          ) : error ? (
            <header className="birth-intro">
              <p className="birth-eyebrow">Shared birth chart</p>
              <h1>{error}</h1>
              <p className="birth-lede">The person who shared it may have taken it down. You can still draw your own below.</p>
            </header>
          ) : !chart ? (
            <p className="birth-wait">Loading the chart…</p>
          ) : (
            <div className="birth-result">
              <header className="birth-intro shared-intro">
                <p className="birth-eyebrow">Shared birth chart</p>
                <h1>{heading}</h1>
                <p className="birth-lede">{line}</p>
                {owner && (
                  <p className="birth-note shared-owner">
                    You shared this chart.{' '}
                    <button type="button" className="birth-text" onClick={stop}>Stop sharing</button>
                  </p>
                )}
              </header>
              <section className="birth-section" aria-labelledby="shared-big-three">
                <h2 id="shared-big-three">{chart.name ? `${chart.name}'s Big Three` : 'The Big Three'}</h2>
                <BigThree placements={chart.placements} timeUnknown={chart.time_unknown} moonChange={chart.moon_change} />
              </section>
              <section className="birth-section" aria-labelledby="shared-reading">
                <h2 id="shared-reading">The chart and reading</h2>
                {chart.time_unknown && (
                  <p className="birth-note">Drawn without a birth time, so it shows signs only, without a rising sign or houses.</p>
                )}
                <div className="birth-read">
                  <div className="birth-figure">
                    <div className="birth-wheel" dangerouslySetInnerHTML={{ __html: chart.svg }} />
                    <ul className="birth-placements shared-placements">
                      {chart.placements.map((placement) => (
                        <li key={placement.label}>
                          <span>{placement.label}</span>
                          <span>
                            {placement.sign} {placement.degree}
                            {placement.house ? ` · House ${placement.house}` : ''}
                            {placement.retrograde ? ' · R' : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {!chart.time_unknown && chart.house_system_label && (
                      <p className="birth-note">{chart.house_system_label} houses</p>
                    )}
                  </div>
                  {wrapReading(data.reading, data.language)}
                </div>
              </section>
              {chart.tarot?.cards?.length > 0 && (
                <section className="birth-section chart-tarot" aria-labelledby="shared-tarot">
                  <h2 id="shared-tarot">{chart.name ? `Tarot for ${chart.name}'s chart` : 'Tarot for this chart'}</h2>
                  <TarotCards cards={chart.tarot.cards} />
                  {chart.tarot.reading && (
                    <div className="chart-tarot-reading">{wrapReading(chart.tarot.reading, data.language)}</div>
                  )}
                </section>
              )}
              {data.qa?.length > 0 && (
                <section className="birth-section birth-ask" aria-labelledby="shared-qa">
                  <h3 id="shared-qa">Questions they asked</h3>
                  {data.qa.map((item, index) => (
                    <div className="birth-qa" key={index}>
                      <p className="birth-qa-question">{item.question}</p>
                      {wrapReading(item.answer, data.language)}
                    </div>
                  ))}
                </section>
              )}
              <section className="birth-section" aria-labelledby="shared-pass">
                <h2 id="shared-pass">Pass it on</h2>
                <ShareRow url={shareUrl} text={`${heading} on NowTarot:`} label="" compact />
              </section>
            </div>
          )}
        </div>

        <section className="birth-section birth-compare shared-cta">
          <h2>{chart?.name ? `Now draw yours, and compare with ${chart.name}` : 'Now draw your own chart'}</h2>
          <p>
            Free, with the full reading. Then compare two charts to see how you fit together.
          </p>
          <p className="birth-note">
            <Link to="/birth-chart-compatibility/">Birth chart compatibility</Link>
            {' · '}
            <Link to="/zodiac-compatibility/">Zodiac sign compatibility</Link>
          </p>
        </section>
        <BirthChartTool embedded />
      </main>
      <Footer />
    </div>
  );
};

export default SharedChart;
